import type {
  DateRange,
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionStatsByVehicleSummaryByPeriod,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
} from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { Configuration, FuelTransaction } from '../..';
import type { FuelTransactionResponse } from '../../__generated__/graphql';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import { toSaveFuelTransactionBody } from './APITransformers';
import type { UpdateTransactionRequest } from './Definitions/APITypes';

/** Panel filters forwarded to `/transactions/report`, mirroring the ones the on-screen queries use. */
export interface TransactionsReportFilters {
  fuelTypes?: string[];
  vehicleIds?: string[];
  vehicleSizes?: string[];
  tagNames?: string[];
  searchQuery?: string;
}

export class NewFuelCardRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getTransactionsHistory = (body: TransactionsHistoryBodyContent): Promise<PagedTransactions> => {
    const requestData: IRequest = {
      endpoint: '/transactions/list',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactions;
    });
  };

  public getTransactionsSummary = (body: TransactionSummaryBodyContent): Promise<TransactionsSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSummary;
    });
  };

  public getTransactionsAvgFuelConsumptionAndCostPerKm = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsAvgFuelConsumptionAndCostPerKm[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/avg-fuel-consumption-and-cost-per-km',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsAvgFuelConsumptionAndCostPerKm[];
    });
  };

  public getTransactionsSumTotalCostByFuelType = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsSumTotalCostByFuelType[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/sum-total-cost-by-fuel-type',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSumTotalCostByFuelType[];
    });
  };

  public getTransactionStatsByVehicleSummary = (body: TransactionSummaryBodyContent): Promise<TransactionStatsByVehicleSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionStatsByVehicleSummary;
    });
  };

  /**
   * Variante por período de `/transactions/stats-by-vehicle/summary`: endpoint dedicado
   * `/stats-by-vehicle/summary/grouped`, com `group_period` obrigatório no body. Retorna
   * um array — um item por bucket (semana/mês). O caller agregado continua em
   * `getTransactionStatsByVehicleSummary` no endpoint sem `/grouped`.
   */
  public getTransactionStatsByVehicleSummaryByPeriod = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionStatsByVehicleSummaryByPeriod[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle/summary/grouped',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionStatsByVehicleSummaryByPeriod[];
    });
  };

  public getTransactionStatsByVehicle = (body: TransactionsHistoryBodyContent): Promise<PagedTransactionStatsByVehicle> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactionStatsByVehicle;
    });
  };

  public getFuelTransaction = async (transactionId: string): Promise<FuelTransaction> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    return camelcaseKeys(response.data, { deep: true }) as FuelTransaction;
  };

  /**
   * The panel filters have to travel with the export. `/transactions/report` reads the very same
   * filter set as the on-screen queries, but treats an absent one as "no filter at all" — so a
   * request carrying only the period yields the unfiltered superset, and the spreadsheet stops
   * agreeing with the numbers the user is looking at (BUG-2780: `Outros` showing up under a
   * Gasolina/Diesel filter, and a plate repeating once per fuel type in the summary tab).
   *
   * Empty filters are omitted rather than sent as `[]`: both mean "no filter" to the backend, and
   * leaving them out keeps an unfiltered export's payload identical to what it has always been.
   */
  public getTransactionsReport = (period: DateRange, vehicleGroupIds: string[], filters?: TransactionsReportFilters): Promise<Blob> => {
    const requestData: IRequest = {
      endpoint: '/transactions/report',
      method: HttpMethod.POST,
      body: JSON.stringify({
        start_date: new Date(period.start),
        end_date: new Date(period.end),
        vehicle_group_ids: vehicleGroupIds,
        ...(filters?.fuelTypes?.length ? { fuel_types: filters.fuelTypes } : {}),
        ...(filters?.vehicleIds?.length ? { vehicle_ids: filters.vehicleIds } : {}),
        ...(filters?.vehicleSizes?.length ? { vehicle_sizes: filters.vehicleSizes } : {}),
        ...(filters?.tagNames?.length ? { tag_names: filters.tagNames } : {}),
        ...(filters?.searchQuery ? { search_query: filters.searchQuery } : {}),
      }),
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  };

  /**
   * Replaces the legacy `PUT /fuel/transactions/deactivate`, which is served by the old Scala
   * frentista-api. Besides the path, three things differ and none of them is cosmetic: the method
   * is POST, the body is a bare array of ids instead of `{ transaction_ids: [...] }`, and the
   * backend answers 204 No Content.
   */
  public deleteTransactions = (transactionIds: string[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/transactions/delete',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionIds),
    };

    return this.requester.request(requestData).then();
  };

  public updateFuelTransaction = (transactionId: string, updateRequest: UpdateTransactionRequest): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.PUT,
      body: JSON.stringify(updateRequest),
    };

    return this.requester.request(requestData).then();
  };

  public createFuelTransaction = (createRequest: FuelTransactionBodyContent): Promise<FuelTransactionResponse> => {
    const transactionBody = toSaveFuelTransactionBody(createRequest);
    const requestData: IRequest = {
      endpoint: '/transactions/create',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionBody),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as FuelTransactionResponse;
    });
  };

  /**
   * Backend `/transactions/create` only accepts JSON, so the odometer photo is encoded the same way
   * as the receipt: base64 bytes inside `odometer_file_base64` (+ matching `odometer_file_content_type`).
   * The `File` is read here so callers can keep passing it as a `File` object without doing the
   * base64 dance themselves.
   */
  public createFuelTransactionWithOdometerFile = async (createRequest: FuelTransactionBodyContent, odometerFile: File): Promise<FuelTransactionResponse> => {
    const { base64, contentType } = await readFileAsBase64(odometerFile);
    return this.createFuelTransaction({
      ...createRequest,
      odometerFileBase64: base64,
      odometerFileContentType: contentType,
    });
  };
}

/** Reads a `File` into a `{base64, contentType}` pair — no `data:...;base64,` prefix. */
const readFileAsBase64 = (file: File): Promise<{ base64: string; contentType: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const commaIdx = result.indexOf(',');
      resolve({
        base64: commaIdx >= 0 ? result.slice(commaIdx + 1) : result,
        contentType: file.type || 'application/octet-stream',
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

export default NewFuelCardRequester;
